// noinspection ES6UnusedImports
import {useEffect, useState} from 'react';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import URL from '@core/scripts/helper/url';
import Logger from '@core/scripts/components/logger';
import Messages from '@core/scripts/components/messages.js';
import MessageBroker from '@bonprix/pattern-library/components/message-broker.js';
import UserAgentCheck from '../3rdparty/util/UserAgentCheck';
import Cookies from 'js-cookie';

const MAX_ATTEMPTS = 3;
const INTERVAL_WAIT_MS = 850;

export default function MarketingParameters({pageType, hurraScriptUrl, traceId}) {
    const [attempt, setAttempts] = useState(1);
    const [firstCall, setFirstCall] = useState(true);
    const [sendOwaParams, setSendOwaParams] = useState(true)
    const [optimusParameters, setOptimusParameters] = useState({});
    const [hurraInstanceId, setHurraInstanceId] = useState('');

    const owaApiUrl = '/ajax/marketing-parameters/';
    let noFetchedParametersAvailable = Object.keys(optimusParameters).length === 0;

    const manualEventPush = () => {
        window.hc_tm = window.hc_tm || [];
        window.hc_tm.push({e: 'track', p: window.owaParams});
    };
    const processOptInMessage = (isOptIn) => {
        if (isOptIn) {
            setAttempts(1);
            setFirstCall(true);
        }
    };
    const sendAddToCartEvent = ({cartSummary, wishlistItemId, eventId, price}) => {
        if (!noFetchedParametersAvailable) {
            const prePageType = window.owaParams.page_type;
            window.owaParams.page_type = 'add_to_cart';
            window.owaParams.product_quantity = 1;
            window.owaParams.eventId = eventId
            window.owaParams.product_price = price
            manualEventPush();
            window.owaParams.page_type = prePageType;
            delete window.owaParams.product_quantity;
        }
    };
    const sendAddToWishlistEvent = (messageData) => {
        if (!noFetchedParametersAvailable) {
            const prePageType = window.owaParams.page_type;
            window.owaParams.eventId = messageData.eventId
            window.owaParams.page_type = 'add_to_wishlist';
            window.owaParams.product_quantity = 1;
            manualEventPush();
            window.owaParams.page_type = prePageType;
            delete window.owaParams.product_quantity;
        }
    };

    useEffect(() => {
        const optinSubscription = MessageBroker.subscribe(Messages.OPTIN.MARKETING_STATUS_CHANGED, (payload) => {
            return processOptInMessage(payload.optIn);
        });
        const allConsentsSubscription = MessageBroker.subscribe(Messages.OPTIN.COMPLETE_CONSENT_GIVEN, () => {
            setSendOwaParams(true)
            noFetchedParametersAvailable = true;
            return acquireOptimusParameters('&forceOptimusCall=true');
        });
        const cartItemAddedSubscription = MessageBroker.subscribe(Messages.CART.ITEM_ADD_SUCCESS, sendAddToCartEvent);
        const wishlistAddSubscription = MessageBroker.subscribe(Messages.WISHLIST.ITEM_ADDED, sendAddToWishlistEvent);
        return () => {
            MessageBroker.unsubscribe(optinSubscription);
            MessageBroker.unsubscribe(cartItemAddedSubscription);
            MessageBroker.unsubscribe(wishlistAddSubscription);
            MessageBroker.unsubscribe(allConsentsSubscription);
        };
    }, []);

    function initHurra() {
        const scriptId = 'hurra-init';
        const script = document.createElement('script');
        if (!document.getElementById(scriptId)) {
            script.id = scriptId;
            script.src = 'http' + ('https:' == document.location.protocol ? 's' : '') + '://ssl.hurra.com/oti.js?cid=' + hurraInstanceId;
            script.async = true;
            script.type = 'text/javascript';
            document.body.appendChild(script);
        } else {
            if (!noFetchedParametersAvailable) {
                manualEventPush();
            }
        }
    }

    const loadHurraScript = (attOptInStatus) => {
        window.owaParams = optimusParameters;
        // for page events, action events will override this
        window.owaParams.eventId = traceId

        const scriptId = 'hurra-init';
        const script = document.createElement('script');
        if (!document.getElementById(scriptId)) {
            script.id = scriptId;
            let ntParam = '';
            let gdpr_att = Cookies.get('gdpr_att');
            if(gdpr_att === undefined) {
                ntParam = attOptInStatus === false ? '&nt=1&gdpr_att=0' : '';
            }
            let scriptUrl = `${hurraScriptUrl}` || URL.toAbsolute('hc_scripts');
            script.src = scriptUrl + '/oti.js?cid=' + hurraInstanceId + ntParam;
            script.async = true;
            script.type = 'text/javascript';
            document.body.appendChild(script);
        } else {
            if (!noFetchedParametersAvailable) {
                manualEventPush();
            }
        }
    };

    const resetCookies = () => {
        Cookies.remove('_con_analytics');
        Cookies.remove('_con_personal');
        Cookies.remove('_con_marketing');
    };

    const acquireAttStatus = () => {
        if (UserAgentCheck.isIOsApp(window.navigator.userAgent)) {

            fetchWithCSRF(URL.toAbsolute(`/ajax/app-permission/`), {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
            })
                .then((response) => {
                    if (response.ok) {
                        return response.text();
                    }
                    return null;
                })
                .then((data) => {
                    if (data !== null) {
                        let json = JSON.parse(data);
                        loadHurraScript(json === 'ALLOWED');
                    } else {
                        loadHurraScript(false);
                        resetCookies();
                    }
                })
                .catch((error) => {
                    Logger.error('Could not fetch permission to load hurra for app', error);
                    loadHurraScript(false);
                });

        } else {
            loadHurraScript(true);
        }
    };

    const acquireOptimusParameters = (optionalParameter) => {
        optionalParameter = optionalParameter || ''
        if (noFetchedParametersAvailable && attempt < MAX_ATTEMPTS) {
            setAttempts(attempt + 1);
            const fetchUrl = URL.toAbsolute(owaApiUrl) + `?bffPageType=${pageType}&traceId=${traceId}`+ optionalParameter;
            fetchWithCSRF(fetchUrl, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
            })
                .then((response) => {
                    if (response.ok) {
                        return response.text();
                    }
                    return null;
                })
                .then((data) => {
                    if (data !== '' && data !== null) {
                        const jsonData = JSON.parse(data);
                        setOptimusParameters(jsonData.parameters);
                        setHurraInstanceId(jsonData.hurraInstanceId);
                        noFetchedParametersAvailable = false;
                        if (hurraInstanceId !== '' && hurraInstanceId !== undefined) {
                            acquireAttStatus();
                        }
                    }
                })
                .catch((error) => {
                    Logger.debug('unable to fetch optimus parameters within ' + attempt + ' attempts', error);
                });
        }
    };

    useEffect(() => {
        if (firstCall) {
            setFirstCall(false);
            acquireOptimusParameters();
        }
        const interval = setInterval(acquireOptimusParameters, INTERVAL_WAIT_MS);
        return () => {
            return clearInterval(interval);
        };
    });

    if (hurraInstanceId !== undefined && hurraInstanceId !== '') {
        acquireAttStatus();
    }
}
